import { useTldrawApp } from "pages/flyer/builder/drawer/hooks";
import { styled } from "pages/flyer/builder/drawer/styles";
import type { TDSnapshot } from "pages/flyer/builder/drawer/types";
import * as React from "react";

const loadingSelector = (s: TDSnapshot) => s.appState.isLoading;

export const LoadingUI = (): any => {
  const loadingMessages = [
    "Building your design",
    "Crafting your masterpiece",
    "Arranging elements perfectly",
    "Making it pixel perfect",
    "Adding creative touches",
    "Polishing the details",
    "Bringing ideas to life",
    "Preparing your canvas",
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
      <StyledFullscreenLoader>
        <StyledLoaderContent>
          <StyledCanvasAnimation>
            {/* Canvas/Paper */}
            <StyledCanvas />

            {/* Animated design elements */}
            <StyledShape className="shape-1" />
            <StyledShape className="shape-2" />
            <StyledShape className="shape-3" />
            <StyledText className="text-1" />
            <StyledText className="text-2" />
            <StyledImage className="image-1" />

            {/* Pencil/Pen drawing */}
            <StyledPencil />
          </StyledCanvasAnimation>

          <StyledLoadingText key={currentMessageIndex}>
            <span>{loadingMessages[currentMessageIndex]}</span>
          </StyledLoadingText>
        </StyledLoaderContent>
      </StyledFullscreenLoader>
    </>
  );
};

export function Loading() {
  const app = useTldrawApp();
  const isLoading = app.useStore?.(loadingSelector) || false;

  if (!isLoading) return null;

  return <LoadingUI />;
}

// CSS Animation strings
const animationStyles = `
  @keyframes fadeIn {
    0% { opacity: 0; transform: scale(0.8); }
    100% { opacity: 1; transform: scale(1); }
  }
  
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translateY(10px); }
    20% { opacity: 1; transform: translateY(0); }
    80% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-10px); }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes pencilDraw {
    0% { transform: translate(20px, 20px) rotate(45deg); }
    25% { transform: translate(80px, 20px) rotate(45deg); }
    50% { transform: translate(80px, 80px) rotate(135deg); }
    75% { transform: translate(20px, 80px) rotate(225deg); }
    100% { transform: translate(20px, 20px) rotate(405deg); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  
  @keyframes dotBounce {
    0%, 80%, 100% { transform: scale(0); opacity: 0; }
    40% { transform: scale(1); opacity: 1; }
  }
  
  @keyframes popIn {
    0% { transform: scale(0) rotate(180deg); opacity: 0; }
    50% { transform: scale(1.2) rotate(90deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }
`;

// Styled components
const StyledFullscreenLoader = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  backdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  animation: "fadeIn 0.3s ease-out",
});

const StyledLoaderContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "40px",
});

const StyledCanvasAnimation = styled("div", {
  position: "relative",
  width: "200px",
  height: "200px",
  background: "linear-gradient(135deg, #EE4360 0%, #ff6b82 100%)",
  borderRadius: "20px",
  boxShadow: "0 20px 40px rgba(238, 67, 96, 0.3)",
  overflow: "hidden",
  animation: "float 3s ease-in-out infinite",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
    animation: "shimmer 2s linear infinite",
  },
});

const StyledCanvas = styled("div", {
  position: "absolute",
  top: "20px",
  left: "20px",
  right: "20px",
  bottom: "20px",
  backgroundColor: "white",
  borderRadius: "10px",
  boxShadow: "inset 0 2px 10px rgba(0, 0, 0, 0.1)",
});

const StyledShape = styled("div", {
  position: "absolute",
  backgroundColor: "rgba(238, 67, 96, 0.7)",
  borderRadius: "4px",

  "&.shape-1": {
    width: "40px",
    height: "40px",
    top: "40px",
    left: "40px",
    animation: "popIn 0.6s ease-out 0.2s both",
  },

  "&.shape-2": {
    width: "30px",
    height: "30px",
    top: "90px",
    right: "50px",
    backgroundColor: "rgba(255, 107, 130, 0.7)",
    borderRadius: "50%",
    animation: "popIn 0.6s ease-out 0.4s both",
  },

  "&.shape-3": {
    width: "50px",
    height: "25px",
    bottom: "60px",
    left: "60px",
    backgroundColor: "rgba(238, 67, 96, 0.5)",
    animation: "popIn 0.6s ease-out 0.6s both",
  },
});

const StyledText = styled("div", {
  position: "absolute",
  backgroundColor: "rgba(156, 163, 175, 0.3)",
  borderRadius: "2px",

  "&.text-1": {
    width: "60px",
    height: "4px",
    top: "50px",
    right: "40px",
    animation: "popIn 0.6s ease-out 0.8s both",
  },

  "&.text-2": {
    width: "45px",
    height: "4px",
    top: "58px",
    right: "40px",
    animation: "popIn 0.6s ease-out 1s both",
  },
});

const StyledImage = styled("div", {
  position: "absolute",
  backgroundColor: "rgba(238, 67, 96, 0.6)",

  "&.image-1": {
    width: "35px",
    height: "35px",
    bottom: "40px",
    right: "40px",
    borderRadius: "6px",
    animation: "popIn 0.6s ease-out 1.2s both",
  },
});

const StyledPencil = styled("div", {
  position: "absolute",
  width: "20px",
  height: "4px",
  backgroundColor: "#4b5563",
  borderRadius: "2px",
  animation: "pencilDraw 4s ease-in-out infinite",

  "&::before": {
    content: '""',
    position: "absolute",
    left: "-8px",
    top: "-3px",
    width: "0",
    height: "0",
    borderStyle: "solid",
    borderWidth: "5px 8px 5px 0",
    borderColor: "transparent #EE4360 transparent transparent",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    right: "-4px",
    top: "-1px",
    width: "6px",
    height: "6px",
    backgroundColor: "#EE4360",
    borderRadius: "50%",
  },
});

const StyledLoadingText = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: "18px",
  fontWeight: "500",
  color: "#4b5563",
  letterSpacing: "-0.02em",
  animation: "fadeInOut 2s ease-in-out",

  span: {
    display: "inline-block",
  },
});

const StyledDots = styled("span", {
  display: "inline-flex",
  gap: "2px",

  span: {
    display: "inline-block",
    width: "4px",
    height: "4px",
    backgroundColor: "#EE4360",
    borderRadius: "50%",
    animation: "dotBounce 1.4s ease-in-out infinite",

    "&:nth-child(2)": {
      animationDelay: "0.2s",
    },

    "&:nth-child(3)": {
      animationDelay: "0.4s",
    },
  },
});
